import {
  loginRequest,
  signupRequest,
  forgotPasswordRequest,
  resetPasswordRequest,
  smsLoginRequest
} from "../api/index";
import { handleErrorRequest } from "../helpers/api";
import { setCurrentDealer, setDealers } from "./dealers";
import { setRole } from "./settings";
import { get } from "lodash";
import { createSelector } from "reselect";
import { actions as shoppingCartActions } from "./shoppingCart";
import axios from "axios";

const authState = state => state.auth;
const getValue = key =>
  createSelector(
    authState,
    state => get(state, key)
  );

export const getUser = getValue("user");

export const getJWT = state => get(state, "auth.user.jwt", "");

export const AUTH_ATTEMPT = "auth/AUTH_ATTEMPT";
export const AUTH_SUCCESS = "auth/AUTH_SUCCESS";
export const AUTH_FAILURE = "auth/AUTH_FAILURE";

export const SET_USER = "auth/SET_USER";

export const SET_SUCCESS_FORGOT = "auth/SET_SUCCESS_FORGOT";

export const RESET_STATE = "auth/RESET_STATE";

const authAttempt = () => ({ type: AUTH_ATTEMPT });
const authSuccess = () => ({ type: AUTH_SUCCESS });
const authFailure = failMessage => ({ type: AUTH_FAILURE, failMessage });

const setUser = user => ({ type: SET_USER, user });

export const setSuccessForgot = successMessage => ({
  type: SET_SUCCESS_FORGOT,
  successMessage
});

const login = data => dispatch => {
  dispatch(authAttempt());
  if (data.verification_code) {
    return smsLoginRequest({ session: data }).then(({ response, error }) => {
      if (response) {
        if (
          response.data.staff.length !== 0 &&
          response.data.staff[0].active === false
        ) {
          let error = { response: "inactiveAccount" };
          dispatch(authFailure(handleErrorRequest(error)));
          return Promise.resolve({ error });
        }
        
        dispatch(authSuccess());
        dispatch(setUser(response.data));
        return Promise.resolve({ response });
      } else {
        dispatch(authFailure(handleErrorRequest(error)));
        return Promise.resolve({ error });
      }
    });
  } else {
    return loginRequest({ session: data }).then(({ response, error }) => {
      if (response) {
        if (response.data.sid) {
          return Promise.resolve({ response });
        }
        if (
          response.data.staff.length !== 0 &&
          response.data.staff[0].active === false
        ) {
          let error = { response: "inactiveAccount" };
          dispatch(authFailure(handleErrorRequest(error)));
          return Promise.resolve({ error });
        }
        
        dispatch(authSuccess());
        dispatch(setUser(response.data));
        return Promise.resolve({ response });
      } else {
        dispatch(authFailure(handleErrorRequest(error)));
        return Promise.resolve({ error });
      }
    });
  }
};

const signup = data => dispatch => {
  dispatch(authAttempt());
  return signupRequest({ user: data }).then(({ response, error }) => {
    if (response) {
      dispatch(authSuccess());
      dispatch(setUser(response.data));
      return Promise.resolve({ response });
    } else {
      dispatch(authFailure(handleErrorRequest(error)));
      return Promise.resolve({ error });
    }
  });
};

const forgotPassword = data => (dispatch, getState) => {
  dispatch(authAttempt());
  return forgotPasswordRequest(data).then(({ response, error }) => {
    if (response) {
      dispatch(authSuccess());
      dispatch(
        setSuccessForgot("Reset password instructions have been sent to email")
      );
    } else {
      dispatch(authFailure(handleErrorRequest(error)));
    }
  });
};

const resetPassword = data => (dispatch, getState) => {
  dispatch(authAttempt());
  return resetPasswordRequest({ user: data }).then(({ response, error }) => {
    if (response) {
      dispatch(authSuccess());
      dispatch(setUser(response.data));
      return Promise.resolve({ response });
    } else {
      dispatch(authFailure(handleErrorRequest(error)));
      return Promise.resolve({ error });
    }
  });
};

const logout = (location = { pathname: "", search: "" }) =>
  function logoutActions(dispatch) {
    const { pathname, search } = location;
    dispatch(setUser(null));
    dispatch(setDealers(null));
    dispatch(setCurrentDealer(null));
    dispatch(setRole(null));
    dispatch(shoppingCartActions.resetState([]));
    return Promise.resolve(`?next=${pathname}${search}`);
  };

const setLogoutInterceptor = replace => (dispatch, getState) => {
  axios.interceptors.response.use(
    response => response,
    error => {
      const token = getJWT(getState());
      if (
        !getState().auth.successAuth ||
        (error.response.status === 401 && token)
      ) {
        dispatch(logout());
      }
      return Promise.reject(error);
    }
  );
};

const resetState = () => ({ type: RESET_STATE });

export const actions = {
  authAttempt,
  authSuccess,
  authFailure,
  setSuccessForgot,
  login,
  signup,
  setUser,
  logout,
  resetState,
  forgotPassword,
  resetPassword,
  setLogoutInterceptor
};

const AUTH_ACTION_HANDLERS = {
  [SET_USER]: (state, action) => ({ ...state, user: action.user }),
  [AUTH_ATTEMPT]: (state, action) => ({
    ...state,
    authenticating: true,
    failAuth: null,
    successAuth: false,
    successForgot: null
  }),
  [AUTH_SUCCESS]: (state, action) => ({
    ...state,
    authenticating: false,
    failAuth: null,
    successAuth: true,
    successForgot: null
  }),
  [AUTH_FAILURE]: (state, action) => ({
    ...state,
    authenticating: false,
    failAuth: action.failMessage,
    successAuth: false,
    successForgot: null
  }),
  [SET_SUCCESS_FORGOT]: (state, action) => ({
    ...state,
    successForgot: action.successMessage
  }),
  [RESET_STATE]: (state, action) => ({
    ...state,
    authenticating: false,
    failAuth: null,
    successAuth: false,
    successForgot: null
  })
};

export const initialState = {
  authenticating: false,
  failAuth: null,
  successAuth: false,
  user: null,
  successForgot: null
};

export default function authReducer(state = initialState, action) {
  const handler = AUTH_ACTION_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
